import React, { useState } from 'react';
import styles from './MobileNavigation.module.scss';
import { Drawer } from 'antd';
import { GlobalIcon, IconImage } from 'src/modules/global';
import { Logo } from 'src/modules/layout/components/header/components/Logo';
import { ProductCategoryLocalizedEntity } from 'src/modules/products';
import { MobileMenu } from 'src/modules/layout/components/header/components/MobileMenu';
import { FlagWithLocale } from 'src/modules/layout/components/header/components/FlagWithLocale';
import { Locale, useLocale, OutputChannels } from 'src/modules/locale';
import { FormattedMessage, useIntl } from 'react-intl';
import Link from 'next/link';
import { getImageBreakpointUrl } from 'src/utils/image';
import { NavMainMenuLink } from '../components/NavMainMenu';

export interface MobileNavigationProps {
  isOpen: boolean;
  onClose: () => void;
  productCategories: ProductCategoryLocalizedEntity[];
  magazineLinks: NavMainMenuLink[];
  openSearch: () => void;
  locale: string;
  setLocale: (locale: string) => void;
  europeUrl: string;
  usaUrl: string;
  australiaUrl: string;
}

export const MobileNavigation = ({
  isOpen,
  onClose,
  productCategories,
  magazineLinks,
  openSearch,
  locale,
  setLocale,
  europeUrl,
  usaUrl,
  australiaUrl,
}: MobileNavigationProps) => {
  const [activeMenu, setActiveMenu] = useState<string>(null);
  const intl = useIntl();
  const handleClose = () => {
    setActiveMenu('');
    onClose();
  };

  const NavButtonOpenMenu = ({ menuName, titleId }: { menuName: string; titleId: string }) => (
    <div className={styles.mainButton} onClick={() => setActiveMenu(menuName)}>
      <span>
        <FormattedMessage id={titleId} />
      </span>
      <GlobalIcon image={IconImage.RightArrow} className={styles.mainButtonIcon} size="custom" />
    </div>
  );

  const { outputChannel } = useLocale();
  return (
    <Drawer
      width="85%"
      height="100%"
      placement="left"
      visible={isOpen}
      closable={false}
      headerStyle={{ height: 0, overflow: 'hidden' }}
      style={{ background: 'rgba(0,0,0,.8)' }}
      maskClosable={true}
      onClose={handleClose}
      className={styles.container}>
      <div className={styles.innerContainer}>
        <div className={styles.topContainer}>
          <Logo />
          <div className={styles.closeButton} onClick={handleClose}>
            <GlobalIcon image={IconImage.Close} className={styles.closeButtonIcon} size="custom" />
          </div>
        </div>
        <div className={styles.mainContainer}>
          <div className={styles.buttonsContainer}>
            <div className={styles.mainButtonsContainer}>
              <NavButtonOpenMenu menuName="products" titleId="header/menu_button/products" />
              <NavButtonOpenMenu menuName="magazine" titleId="header/menu_button/magazine" />
              <Link href="/designers">
                <a className={styles.mainButton}>
                  <span>
                    <FormattedMessage id="header/menu_button/designers" />
                  </span>
                </a>
              </Link>
              <Link href="/professionals">
                <a className={styles.mainButton}>
                  <span>
                    <FormattedMessage id="header/menu_button/professionals" />
                  </span>
                </a>
              </Link>
              <Link href="/about">
                <a className={styles.mainButton}>
                  <span>
                    <FormattedMessage id="header/menu_button/about" />
                  </span>
                </a>
              </Link>
            </div>
            <div className={styles.secondaryButtonsContainer}>
              <div className={styles.secondaryButton} onClick={openSearch}>
                <GlobalIcon
                  image={IconImage.Search}
                  className={styles.secondaryButtonIcon}
                  size="custom"
                />
                <FormattedMessage id="header/menu_button/search" />
              </div>
              <Link href="/store">
                <a className={styles.secondaryButton}>
                  <GlobalIcon
                    image={IconImage.StorePin}
                    className={styles.secondaryButtonIcon}
                    size="custom"
                  />
                  <FormattedMessage id="header/menu_button/stores" />
                </a>
              </Link>
              <Link href="/professionals/downloads">
                <a className={styles.secondaryButton}>
                  <GlobalIcon
                    image={IconImage.Download}
                    className={styles.secondaryButtonIcon}
                    size="custom"
                  />
                  <FormattedMessage id="header/menu_button/downloads" />
                </a>
              </Link>
              <Link href="/professionals/contact">
                <a className={styles.secondaryButton}>
                  <GlobalIcon
                    image={IconImage.Contact}
                    className={styles.secondaryButtonIcon}
                    size="custom"
                  />
                  <FormattedMessage id="header/menu_button/contact" />
                </a>
              </Link>
              <div className={styles.localeButtons}>
                <FlagWithLocale
                  className={styles.localeButton}
                  href={europeUrl}
                  flag="DE"
                  label={intl.formatMessage({ id: 'locale/de' })}
                  locale={Locale.DE}
                  selected={locale === Locale.DE}
                  onClick={() => {
                    setLocale(Locale.DE);
                  }}
                  shallow={outputChannel === OutputChannels.DE}
                />
                <FlagWithLocale
                  className={styles.localeButton}
                  href={europeUrl}
                  flag="EN"
                  label={intl.formatMessage({ id: 'locale/en' })}
                  locale={Locale.EN}
                  selected={locale === Locale.EN}
                  onClick={() => {
                    setLocale(Locale.EN);
                  }}
                  shallow={outputChannel === OutputChannels.DE}
                />
                <FlagWithLocale
                  className={styles.localeButton}
                  href={usaUrl}
                  flag="US"
                  label={intl.formatMessage({ id: 'locale/us' })}
                  locale={Locale.EN}
                  selected={locale === Locale.US}
                />
                <FlagWithLocale
                  className={styles.localeButton}
                  href={australiaUrl}
                  flag="AU"
                  label={intl.formatMessage({ id: 'locale/au' })}
                  locale={Locale.EN}
                  selected={locale === Locale.AU}
                />
              </div>
            </div>
          </div>
          <MobileMenu
            name="products"
            title={intl.formatMessage({ id: 'header/menu_button/products' })}
            activeMenu={activeMenu}
            setActiveMenu={setActiveMenu}
            onClose={handleClose}
            cards={productCategories.map((pc) => ({
              imageUrl: getImageBreakpointUrl('xs', pc?.image)?.url,
              title: pc.name,
              linkUrl: `/products/${encodeURIComponent(pc.slug)}`,
            }))}
          />
          <MobileMenu
            name="magazine"
            title={intl.formatMessage({ id: 'header/menu_button/magazine' })}
            activeMenu={activeMenu}
            setActiveMenu={setActiveMenu}
            onClose={handleClose}
            links={magazineLinks}
          />
        </div>
      </div>
    </Drawer>
  );
};
